import {
  AnimatedCounter,
  CardShimmerBar,
} from "@/components/company/company-animations";
import { CompanyWebFigureNote } from "@/components/company/company-web-figures";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/hooks/useTranslation";
import { webFigureText } from "@/lib/company-web-figures";
import { computeYoy, formatCompactAmount } from "@/lib/format-amount";
import { cn } from "@/lib/utils";
import { CompanyDTO, type CompanyWebFigure } from "@shaarp/common";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarChart3Icon,
  BriefcaseIcon,
  CircleDollarSignIcon,
  GlobeIcon,
  InfoIcon,
  SparklesIcon,
  UsersIcon,
} from "lucide-react";
import { RiLinkedinFill } from "react-icons/ri";

type CardCompanyFinancialsProps = {
  company: CompanyDTO;
  /** A section the figures come from still runs: they may change. */
  isRefreshing?: boolean;
  hideGrowthSignals?: boolean;
  /**
   * The revenue and headcount the web profile states, each shown only where
   * the base has neither a figure nor an estimate of its own.
   */
  webFigures?: {
    revenue?: CompanyWebFigure | null;
    headcount?: CompanyWebFigure | null;
  };
};

type FinancialMetric = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  year?: number | null;
  value: number | null;
  stringValue?: string;
  formatFn: (n: number) => string;
  yoyPercentage?: number | null;
  alwaysShow?: boolean;
  isEstimated?: boolean;
  estimatedTooltip?: string;
  /** A figure the web profile states in the base's place: its estimate or source under it. */
  webFigure?: CompanyWebFigure;
};

/** A key figure only the web profile gives: its text, and what backs it. */
function webMetric(
  icon: FinancialMetric["icon"],
  label: string,
  figure: CompanyWebFigure,
): FinancialMetric {
  return {
    icon,
    label,
    value: null,
    stringValue: webFigureText(figure),
    formatFn: String,
    alwaysShow: true,
    webFigure: figure,
  };
}

/** The latest year that states `field`, however old: the base's own figure. */
function latestStated(
  finances: CompanyDTO["business"]["finances"],
  field: "sales" | "headcount",
): CompanyDTO["business"]["finances"][number] | undefined {
  return [...finances]
    .filter((finance) => finance[field] != null)
    .sort((a, b) => b.year - a.year)[0];
}

/** The key figures while their sections run and the company has none yet. */
export function CardCompanyFinancialsSkeleton() {
  return (
    <div className="grid grid-cols-2 @2xl:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="relative overflow-hidden flex items-center gap-3 px-5 py-4 rounded-xl border border-border bg-background"
        >
          <CardShimmerBar />
          <div className="flex flex-col gap-1.5 flex-1">
            <Skeleton variant="subtle" className="h-1.5 w-12" />
            <Skeleton variant="subtle" className="h-2 w-8" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CardCompanyFinancials({
  company,
  isRefreshing = false,
  hideGrowthSignals = false,
  webFigures,
}: CardCompanyFinancialsProps) {
  const { t } = useTranslation();

  const latest = company.business.finances[0];
  const previous = company.business.finances[1];

  const currentYear = new Date().getFullYear();
  const isRecent = latest?.year != null && currentYear - latest.year <= 2;
  // A figure of the base older than two years gives way to its estimate, never
  // to the web's: the web fills only what the base does not state at all.
  const storedSales = latestStated(company.business.finances, "sales");
  const storedHeadcount = latestStated(company.business.finances, "headcount");
  const employeesMin = company.employeesCount?.min;
  const employeesMax = company.employeesCount?.max;

  const metrics: FinancialMetric[] = [];

  // Revenue — show actual if recent, otherwise fallback to estimated revenue
  if (isRecent && latest?.sales != null) {
    metrics.push({
      icon: BarChart3Icon,
      label: t("company.insights.financials.revenue"),
      year: latest.year,
      value: latest.sales,
      formatFn: formatCompactAmount,
      yoyPercentage:
        latest.revenueGrowthRate ?? computeYoy(latest.sales, previous?.sales),
      alwaysShow: true,
    });
  } else if (company.estimatedRevenue?.range) {
    metrics.push({
      icon: BarChart3Icon,
      label: t("company.insights.estimatedRevenue.title"),
      value: null,
      stringValue: company.estimatedRevenue.range,
      formatFn: formatCompactAmount,
      alwaysShow: true,
      isEstimated: true,
      estimatedTooltip: t("company.insights.estimatedRevenue.tooltip"),
    });
  } else if (storedSales?.sales != null) {
    metrics.push({
      icon: BarChart3Icon,
      label: t("company.insights.financials.revenue"),
      year: storedSales.year,
      value: storedSales.sales,
      formatFn: formatCompactAmount,
      alwaysShow: true,
    });
  } else if (webFigures?.revenue) {
    metrics.push(
      webMetric(
        BarChart3Icon,
        t("company.insights.financials.revenue"),
        webFigures.revenue,
      ),
    );
  } else {
    metrics.push({
      icon: BarChart3Icon,
      label: t("company.insights.financials.revenue"),
      year: undefined,
      value: null,
      formatFn: formatCompactAmount,
      alwaysShow: true,
    });
  }

  // Net result — always shown
  metrics.push({
    icon: CircleDollarSignIcon,
    label: t("company.insights.financials.netResult"),
    year: isRecent ? latest?.year : undefined,
    value: isRecent && latest?.result != null ? latest.result : null,
    formatFn: formatCompactAmount,
    yoyPercentage:
      isRecent && latest?.result != null
        ? computeYoy(latest.result, previous?.result)
        : null,
    alwaysShow: true,
  });

  // Employees — always shown. The count of the base stands whatever the age
  // of the accounts; only a recent year is compared with the one before.
  const recentHeadcount = isRecent ? (latest?.headcount ?? null) : null;
  const employeesValue =
    recentHeadcount ?? company.employeesCount?.exact ?? null;
  const prevEmployees = isRecent ? (previous?.headcount ?? null) : null;
  const employeesYoy =
    employeesValue != null
      ? (company.growthSignals.employees?.changeYearlyPercentage ??
        computeYoy(employeesValue, prevEmployees))
      : null;

  if (employeesValue != null) {
    metrics.push({
      icon: UsersIcon,
      label: t("company.insights.financials.employees"),
      year: recentHeadcount != null ? latest?.year : undefined,
      value: employeesValue,
      formatFn: (n) => n.toLocaleString("fr-FR"),
      yoyPercentage: employeesYoy,
      alwaysShow: true,
    });
  } else if (company.employeesCount?.estimatedRange) {
    metrics.push({
      icon: UsersIcon,
      label: t("company.insights.financials.employees"),
      value: null,
      stringValue: `~ ${company.employeesCount.estimatedRange}`,
      formatFn: (n) => n.toLocaleString("fr-FR"),
      alwaysShow: true,
      isEstimated: true,
      estimatedTooltip: t("company.insights.estimatedEmployees.tooltip"),
    });
  } else if (storedHeadcount?.headcount != null) {
    metrics.push({
      icon: UsersIcon,
      label: t("company.insights.financials.employees"),
      year: storedHeadcount.year,
      value: storedHeadcount.headcount,
      formatFn: (n) => n.toLocaleString("fr-FR"),
      alwaysShow: true,
    });
  } else if (employeesMin != null && employeesMax != null && employeesMax > 0) {
    // The bounds of a bracket (INSEE's, or the web profile's), not a count:
    // marked as the estimated range above is.
    metrics.push({
      icon: UsersIcon,
      label: t("company.insights.financials.employees"),
      value: null,
      stringValue: `~ ${employeesMin}–${employeesMax}`,
      formatFn: (n) => n.toLocaleString("fr-FR"),
      alwaysShow: true,
      isEstimated: true,
      estimatedTooltip: t("company.insights.estimatedEmployees.tooltip"),
    });
  } else if (webFigures?.headcount) {
    metrics.push(
      webMetric(
        UsersIcon,
        t("company.insights.financials.employees"),
        webFigures.headcount,
      ),
    );
  } else {
    metrics.push({
      icon: UsersIcon,
      label: t("company.insights.financials.employees"),
      year: undefined,
      value: null,
      formatFn: (n) => n.toLocaleString("fr-FR"),
      alwaysShow: true,
    });
  }

  if (!hideGrowthSignals) {
    // Growth signals: LinkedIn followers
    const linkedinFollowers = company.growthSignals?.linkedinFollowers;
    if (linkedinFollowers?.current != null) {
      metrics.push({
        icon: RiLinkedinFill,
        label: t("company.signals.growthSignals.linkedinFollowers"),
        value: linkedinFollowers.current,
        formatFn: (n) => n.toLocaleString("fr-FR"),
        yoyPercentage: linkedinFollowers.changeYearlyPercentage ?? null,
      });
    }

    // Growth signals: Job postings
    const jobPostings = company.growthSignals?.jobPostings;
    if (jobPostings?.current != null) {
      metrics.push({
        icon: BriefcaseIcon,
        label: t("company.signals.growthSignals.jobPostings"),
        value: jobPostings.current,
        formatFn: (n) => n.toLocaleString("fr-FR"),
        yoyPercentage: jobPostings.changeYearlyPercentage ?? null,
      });
    }

    // Growth signals: Website visits
    const websiteVisits = company.growthSignals?.websiteVisits;
    if (websiteVisits?.current != null) {
      metrics.push({
        icon: GlobeIcon,
        label: t("company.signals.growthSignals.websiteVisits"),
        value: websiteVisits.current,
        formatFn: (v) => {
          if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
          if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
          return v.toString();
        },
        yoyPercentage: websiteVisits.changeYearlyPercentage ?? null,
      });
    }
  }

  const visibleMetrics = metrics.filter(
    (m) => m.alwaysShow || m.value != null || m.stringValue,
  );

  const tooltipKey =
    company.enrichmentLevel >= 2
      ? "components.dataTableCompanies.columns.notFoundByAiTooltip"
      : "components.dataTableCompanies.columns.notEnrichedTooltip";

  return (
    <div className="grid grid-cols-2 @2xl:grid-cols-3 gap-4">
      {visibleMetrics.map((metric) => (
        <div
          key={metric.label}
          className="relative overflow-hidden flex items-center gap-3 px-5 py-4 rounded-xl border border-border bg-background"
        >
          {isRefreshing && <CardShimmerBar />}
          {metric.isEstimated && metric.estimatedTooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="absolute top-2 right-2 size-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {metric.estimatedTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-muted">
            <metric.icon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground font-medium truncate">
                {metric.label}
                {metric.value != null && metric.year != null && (
                  <span className="ml-1 opacity-60">{metric.year}</span>
                )}
              </span>
              {metric.value != null && metric.yoyPercentage != null && metric.yoyPercentage !== 0 && Math.round(metric.yoyPercentage) !== 0 && (
                <span
                  className={cn(
                    "flex items-center gap-0.5 text-xs font-medium rounded-full px-2 py-0.5 shrink-0",
                    metric.yoyPercentage > 0
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
                  )}
                >
                  {metric.yoyPercentage > 0 ? (
                    <ArrowUpIcon className="w-3 h-3" />
                  ) : (
                    <ArrowDownIcon className="w-3 h-3" />
                  )}
                  {metric.yoyPercentage > 0 ? "+" : ""}
                  {Math.round(metric.yoyPercentage)} %
                </span>
              )}
            </div>
            {metric.value != null ? (
              <span className="text-lg font-bold leading-tight">
                <AnimatedCounter
                  value={metric.value}
                  formatFn={metric.formatFn}
                />
              </span>
            ) : metric.stringValue ? (
              <>
                <span className="text-lg font-bold leading-tight">
                  {metric.stringValue}
                </span>
                {metric.webFigure && (
                  <CompanyWebFigureNote figure={metric.webFigure} />
                )}
              </>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="flex items-center gap-1 text-muted-foreground/50 cursor-help">
                      <span>—</span>
                      <SparklesIcon className="size-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    {t(tooltipKey)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
